import { useMutation } from '@apollo/client';
import {
  CheckCircleIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  DocumentMagnifyingGlassIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import {
  UnitTransferDocumentFulfilmentModeEnumType,
  UnitTransferDocumentStatusEnumType,
  UnitTransferDocumentTypeEnumType,
} from '__generated__/graphql';
import { ElementType, FC } from 'react';

import ErrorMessage from 'components/ErrorMessage';

import Alert from 'primitives/Alert';
import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import UploadInput from 'primitives/UploadInput';

import unitTransferDocumentStatusToReadable from 'utils/enums/unit-transfer-document-status-to-readable';
import unitTransferDocumentTypeToReadable from 'utils/enums/unit-transfer-document-type-to-readable';

const UPLOAD_UNIT_TRANSFER_DOCUMENT_MUTATION = gql(`
  mutation UploadUnitTransferDocument($requestId: ID!, $type: UnitTransferDocumentTypeEnumType!, $file: Upload!) {
    uploadUnitTransferDocument(requestId: $requestId, type: $type, file: $file) {
      id
      status
    }
  }
`);

const REMOVE_UNIT_TRANSFER_DOCUMENT_MUTATION = gql(`
  mutation RemoveUnitTransferDocument($documentId: ID!) {
    removeUnitTransferDocument(documentId: $documentId) {
      id
      status
    }
  }
`);

export type ChecklistDocument = {
  id: string;
  type: any;
  name: string;
  status: UnitTransferDocumentStatusEnumType;
  fulfilmentMode: UnitTransferDocumentFulfilmentModeEnumType;
  isRequired: boolean;
  rejectionReason?: string | null;
  url?: string | null;
  blankTemplateUrl?: string | null;
};

// Shown under the title for the document types where "which file do I upload" isn't obvious from
// the name alone.
const getDocumentTypeHelpText = (type: UnitTransferDocumentTypeEnumType): string | null => {
  switch (type) {
    case UnitTransferDocumentTypeEnumType.TransfereeNetWorthCertificate:
      return "A net worth certificate (from a Chartered Accountant) confirming the transferee meets SEBI's accredited investor criteria — broadly, ₹5 crore+ net worth (with ₹2.5 crore+ in financial assets) alongside ₹1 crore+ annual income, or ₹7.5 crore+ net worth (₹3.75 crore+ in financial assets) on its own, for individuals/HUFs/family trusts; ₹50 crore+ net worth for other trusts and body corporates.";
    case UnitTransferDocumentTypeEnumType.ReasonEvidence:
      return 'Documentary evidence for the stated reason — e.g. a death certificate and legal heir/succession certificate for death, a medical certificate for disability, or a board resolution/merger order for entity or family office restructuring.';
    case UnitTransferDocumentTypeEnumType.ValuationReport:
      return 'A valuation report for the units being sold, uploaded by the transferor.';
    default:
      return null;
  }
};

// PENDING_UPLOAD is the DB's way of saying "this checklist row has no file yet", but for a
// document nobody will ever upload it reads as a task the investor is failing to do. Both of
// these arrive by another route entirely — Leegality at signing, or ops — so the badge says who
// it is waiting on instead.
export const getStatusBadge = (document: ChecklistDocument): { label: string; color: string } => {
  if (document.status !== UnitTransferDocumentStatusEnumType.PendingUpload)
    return unitTransferDocumentStatusToReadable(document.status);

  if (document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.Signature)
    return { label: 'Awaiting e-signature', color: 'gray' };

  if (document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.AdminUpload)
    return { label: 'Provided by AngelList India', color: 'gray' };

  return unitTransferDocumentStatusToReadable(document.status);
};

// Mirrors getStatusBadge's branching exactly, so the icon and the badge always agree about what
// state a document is in — just rendered as a large illustration-style badge instead of a pill.
const getStatusIcon = (
  document: ChecklistDocument
): { icon: ElementType; badgeBg: string; iconColor: string } => {
  if (document.status === UnitTransferDocumentStatusEnumType.Approved)
    return { icon: CheckCircleIcon, badgeBg: 'bg-green-50', iconColor: 'text-green-500' };

  if (document.status === UnitTransferDocumentStatusEnumType.Rejected)
    return { icon: XCircleIcon, badgeBg: 'bg-red-50', iconColor: 'text-red-500' };

  if (document.status === UnitTransferDocumentStatusEnumType.AiChecked)
    return {
      icon: DocumentMagnifyingGlassIcon,
      badgeBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
    };

  if (document.status === UnitTransferDocumentStatusEnumType.Uploaded)
    return { icon: DocumentMagnifyingGlassIcon, badgeBg: 'bg-sky-50', iconColor: 'text-sky-500' };

  // PENDING_UPLOAD: who it's waiting on decides the icon, same as getStatusBadge.
  if (document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.InvestorUpload)
    return { icon: DocumentArrowUpIcon, badgeBg: 'bg-indigo-50', iconColor: 'text-indigo-500' };

  return { icon: ClockIcon, badgeBg: 'bg-gray-100', iconColor: 'text-gray-500' };
};

const DocumentChecklistItem: FC<{
  document: ChecklistDocument;
  requestId: string;
  onChanged: () => void;
  // Mirrors the backend's REMOVABLE_REQUEST_STATUSES / UPLOADABLE_REQUEST_STATUSES — both are the
  // same three statuses as the submit gate, so one flag covers upload, replace and remove here.
  canManageDocuments: boolean;
}> = ({ document, requestId, onChanged, canManageDocuments }) => {
  const { label: statusLabel, color: statusColor } = getStatusBadge(document);

  const [uploadDocument, { loading: uploading, error: uploadError }] = useMutation(
    UPLOAD_UNIT_TRANSFER_DOCUMENT_MUTATION
  );
  const [removeDocument, { loading: removing, error: removeError }] = useMutation(
    REMOVE_UNIT_TRANSFER_DOCUMENT_MUTATION
  );

  const isUploadable =
    document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.InvestorUpload;
  const canReplace =
    canManageDocuments &&
    document.status !== UnitTransferDocumentStatusEnumType.Approved &&
    document.status !== UnitTransferDocumentStatusEnumType.Superseded;
  const helpText = getDocumentTypeHelpText(document.type);
  const { icon: StatusIcon, badgeBg, iconColor } = getStatusIcon(document);

  return (
    <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50/60 px-5 py-4 rounded-t-lg">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {unitTransferDocumentTypeToReadable(document.type)}
            {/* Matches the admin app: the exception is called out, not the rule. */}
            {!document.isRequired ? (
              <span className="text-sm font-normal text-gray-500"> (optional)</span>
            ) : null}
          </p>
          {helpText ? <p className="mt-1 text-xs text-gray-500">{helpText}</p> : null}
        </div>
        <Badge label={statusLabel} color={statusColor} showIndicator={true} />
      </div>

      <div className="flex items-center gap-4 px-5 py-5">
        <div
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${badgeBg}`}
        >
          <StatusIcon className={`h-6 w-6 ${iconColor}`} />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          {document.status === UnitTransferDocumentStatusEnumType.Rejected &&
          document.rejectionReason ? (
            <Alert
              type="error"
              title="This document was sent back"
              message={document.rejectionReason}
            />
          ) : null}

          {isUploadable ? (
            <div>
              {document.url ? (
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={document.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-indigo-700 hover:underline"
                  >
                    View uploaded file
                  </a>
                  {canReplace ? (
                    <Button
                      variant="secondary"
                      loading={removing}
                      onClick={() =>
                        removeDocument({ variables: { documentId: document.id } }).then(onChanged)
                      }
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              ) : canManageDocuments ? (
                <>
                  {document.blankTemplateUrl ? (
                    <a
                      className="mb-2 inline-block w-fit text-sm text-indigo-700 hover:underline"
                      href={document.blankTemplateUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download blank template
                    </a>
                  ) : null}
                  {/* isPdf is deliberately left off rather than set false: without it UploadInput
                      accepts images and spreadsheets too, and most of this checklist (PAN, Aadhaar,
                      CML copy) is something the investor photographs rather than a generated PDF. */}
                  <UploadInput
                    label="Upload document"
                    value={null}
                    maxFileSize={20}
                    onChange={([file]) => {
                      if (!file) return;
                      uploadDocument({
                        variables: { requestId, type: document.type, file },
                      }).then(onChanged);
                    }}
                  />
                  {/* Not `readOnly={uploading}` — UploadInput's readOnly branch renders a bare "—"
                      when value is null, so the drop zone would vanish mid-upload and read as a
                      crash. The upload is fast enough that a status line is the honest signal. */}
                  {uploading ? <p className="mt-2 text-sm text-gray-500">Uploading…</p> : null}
                </>
              ) : (
                // Documents can only be added while the checklist is still open (same gate as
                // Submit) — an upload box here would just fail, so show status instead of a control.
                <p className="text-sm text-gray-500">Not uploaded.</p>
              )}
            </div>
          ) : null}

          {/* Documents fulfilled by ops or by e-signature only ever need a link once they exist.
              Before that, the status badge alone ("Provided by AngelList India" / "Awaiting
              e-signature") left this blank — the icon with nothing next to it read as broken. */}
          {!isUploadable ? (
            document.url ? (
              <a
                href={document.url}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-sm text-indigo-700 hover:underline"
              >
                View document
              </a>
            ) : (
              <p className="text-sm text-gray-500">
                {document.fulfilmentMode === UnitTransferDocumentFulfilmentModeEnumType.Signature
                  ? "Not ready yet — you'll be asked to sign this once it's your turn."
                  : 'Not ready yet — AngelList India will provide this document.'}
              </p>
            )
          ) : null}

          {uploadError ? <ErrorMessage error={uploadError} type="alert" /> : null}
          {removeError ? <ErrorMessage error={removeError} type="alert" /> : null}
        </div>
      </div>
    </div>
  );
};

export default DocumentChecklistItem;
